import { FormikContextType } from 'formik';
import { TokenGroupProps } from '@amzn/awsui-components-react-v3';
import * as yup from 'yup';
import i18n from '../../i18n';
import {
  ListContainerDefinitionsResponse,
  GetLocationsForSiteResponse,
  ContainerDefinition,
} from '../../services/container-management/types';

export type Container = {
  containerId: string;
  containerType: string;
  containerAssetTag: string;
  scannableId: string;
  serials: TokenGroupProps.Item[];
  serial: string;
};

export type MetadataLocationRoom = {
  name: string;
  equipmentId: string;
  zone: string;
};

export type MetadataLocationSite = {
  name: string;
  equipmentId: string;
};

export type ParentContainer = {
  id: string;
  type: string;
  name: string;
  fq_name: string;
};

export type Rule = {
  name: string;
  policyId: string;
};

export type PolicySearchResult = {
  rule: Rule[];
};

export type ContainerSearchResult = {
  containerId: string;
  name: string;
  containerType: string;
  category: string;
  subcategory: string;
  cctvRequired: string;
  size: string;
  isActive: string;
  state: string;
  lastUpdatedUser: string;
  createdBy: string;
  lastUpdatedTime: string;
  creationTime: string;
  containerDefinitionId: string;
  locationId: string;
  site: MetadataLocationSite;
  room: MetadataLocationRoom;
  section: string;
  aisle: string;
  level: string;
  alphabetIdentifier: string;
  cell: string;
  seal_ids: string[];
  ticket_ids: string[];
  parent_container: ParentContainer;
  userAlias: string;
  move_support: string;
  policy: PolicySearchResult;
  hasOverriddenRules: boolean;
  isCompliant: boolean | undefined;
  isEmpty: boolean;
  current_version: string;
};

export type FormikState = FormikContextType<Container>;

export type Category = {
  subcategories: Subcategory[];
};

export type Subcategory = {
  subcategory: string;
  containerDefinitionId: string;
};

export type ContainerDefinitionResults = {
  categoryMap: Map<string, Category>;
  containerDefinitionMap: Map<string, ContainerDefinition>;
};

export type ContainerMoveState = {
  form: FormikState;
};

export type ContainerRule = {
  rule: ContainerBlock;
  enabled: boolean;
  isOverride: boolean;
  policyId: string | null;
};

export enum ContainerBlock {
  CycleCountBlock = 'CC Block',
  StowBlock = 'Stow Block',
  PickBlock = 'Pick Block',
}

export enum PolicyAction {
  CycleCount = 'CycleCount',
  Stow = 'Stow',
  Pick = 'Pick',
}

export enum PolicyEffect {
  Allow = 'Allow',
  Deny = 'Deny',
}

export const InitialValues = {
  containerId: '',
  containerType: '',
  containerAssetTag: '',
  scannableId: '',
  serials: [],
  serial: '',
};

export const ContainerFormSchema = yup.object().shape({
  containerId: yup.string().max(256, 'Container Id must be less than 256 characters'),
  containerType: yup.string().max(256, 'Container type must be less than 256 characters'),
  containerAssetTag: yup
    .string()
    .required(i18n.t('A Pallet Asset Tag is required'))
    .max(20, 'Asset Tag must be less than 20 characters')
    .matches(/^[a-z0-9-_]+$/i, { message: i18n.t('Provide a valid Asset Tag') }),
  scannableId: yup.string().max(256, 'Scannable id must be less than 256 characters'),
  serials: yup.array().of(yup.mixed().transform((item) => item && item.label)),
  serial: yup
    .string()
    .required(i18n.t('A Tote Asset Tag is required'))
    .max(20, 'Asset Tag must be less than 20 characters')
    .matches(/^[a-z0-9-_]+$/i, { message: i18n.t('Provide a valid Asset Tag') }),
});

export type ListContainerDefinitions = ListContainerDefinitionsResponse;

export type GetLocationsForSite = GetLocationsForSiteResponse;
